import { useIndexQuery } from 'api';
import { useCurrentPharmacyOrThrow, useCurrentResource, useCurrentRoleOrThrow } from 'hooks/slices';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useGetPanelistConnectionStatus } from 'pages/pharmacy/PanelistConnection/PharmacyPanelistConnectionPage/PharmacyPanelistConnectionPage';
import type { PharmacyPanelist } from 'types/api/pharmacies/panelist_connection';
import type { GetKonnectConfigResponse } from 'types/api/pharmacies/trades/trade';
import type { BeneficiaryAccount } from 'types/api/shared/beneficiary_accounts';
import type { GetLegalEntityResponse } from 'types/api/shared/legal_entities';

/**
 * Status shown next to a Paramètres entry:
 * - `ok`      → everything configured (green tick ✅)
 * - `action`  → an error or something left to configure (red exclamation ⚠️)
 * - `pending` → configuration is in progress on our side (orange clock 🕚)
 * - `undefined` → no badge for this entry (or its data isn't loaded yet)
 */
export type SettingsStatus = 'ok' | 'action' | 'pending';

export type SettingsKey =
  | 'profile'
  | 'pharmacy'
  | 'banking'
  | 'billing'
  | 'group'
  | 'team'
  | 'konnect'
  | 'kopilot'
  | 'extractor'
  | 'stripe'
  | 'note_templates'
  | 'preferences';

export type SettingsEntryState = {
  visible: boolean;
  status?: SettingsStatus;
};

export type SettingsStatuses = {
  entries: Record<SettingsKey, SettingsEntryState>;
  actionCount: number;
};

const konnectStatus = (config?: GetKonnectConfigResponse): SettingsStatus | undefined => {
  if (!config?.konnect_client) return undefined;
  if (config.onboarding_finished) return 'ok';
  if (config.pms_with_error || config.lab_portal_with_errors.length > 0) return 'action';
  return 'pending';
};

const bankingStatus = (accounts?: BeneficiaryAccount[]): SettingsStatus | undefined => {
  if (!accounts) return undefined;
  const currentAccount = accounts.find((account) => account.current);
  if (!currentAccount) return 'action';
  if (currentAccount.status === 'active') return 'ok';
  if (currentAccount.status === 'pending') return 'pending';
  return 'action';
};

const billingStatus = (data?: GetLegalEntityResponse): SettingsStatus | undefined => {
  if (!data) return undefined;
  return data.legal_entity?.valid ? 'ok' : 'action';
};

const extractorStatus = (panelist?: PharmacyPanelist | null): SettingsStatus => {
  if (!panelist?.panelist) return 'action';
  if (panelist.status === 'sharing_not_approved' || panelist.status === 'not_client')
    return 'action';
  if (panelist.status === 'establishing') return 'pending';
  return 'ok';
};

export const useSettingsStatuses = (): SettingsStatuses => {
  const currentRole = useCurrentRoleOrThrow();
  const currentResource = useCurrentResource();
  const pharmacy = useCurrentPharmacyOrThrow();
  const isPharmacyOwner = currentRole.kind === 'pharmacy_owner';

  const { data: pharmacySettingsData } = useIndexQuery('pharmacies/pharmacies/current/setting', {});
  const pharmacySetting = pharmacySettingsData?.pharmacy_setting;
  const isKopilotInvoiceEnabled = pharmacySetting?.is_real_invoice_enabled ?? false;

  const stripeStatus =
    currentResource && 'stripe_account_status' in currentResource
      ? currentResource.stripe_account_status
      : 'none';

  const { panelistConnection, isPharmacyAllowedToEstablishConnection } =
    useGetPanelistConnectionStatus();

  const isKopilotVisible = isKopilotInvoiceEnabled;
  const isStripeVisible = stripeStatus !== 'none';
  const { isEnabled: isPreferencesVisible } = useFeatureFlag('can_pharmacy_choose_auto_transmit');

  const { data: konnectData } = useIndexQuery('pharmacies/trades/konnect/config/mine', {});
  const { data: bankingData } = useIndexQuery('shared/payment/beneficiary_accounts', {});
  const { data: legalEntityData } = useIndexQuery(
    'shared/legal_entities/current',
    { resource_id: pharmacy.id, resource_type: 'Pharmacy' },
    { enabled: isPharmacyOwner }
  );

  const entries: Record<SettingsKey, SettingsEntryState> = {
    profile: { visible: true },
    pharmacy: { visible: true },
    group: { visible: true },
    team: { visible: true },
    note_templates: { visible: true },
    banking: {
      visible: true,
      status: bankingStatus(bankingData?.beneficiary_accounts),
    },
    billing: { visible: true, status: billingStatus(legalEntityData) },
    konnect: { visible: true, status: konnectStatus(konnectData) },
    kopilot: {
      visible: isKopilotVisible,
      status: isKopilotVisible ? 'ok' : undefined,
    },
    extractor: {
      visible: isPharmacyAllowedToEstablishConnection,
      status: isPharmacyAllowedToEstablishConnection
        ? extractorStatus(panelistConnection?.pharmacy_panelist)
        : undefined,
    },
    stripe: {
      visible: isStripeVisible,
      status: isStripeVisible ? (stripeStatus === 'ok' ? 'ok' : 'action') : undefined,
    },
    preferences: { visible: isPreferencesVisible },
  };

  const actionCount = Object.values(entries).filter(
    (entry) => entry.visible && entry.status === 'action'
  ).length;

  return { entries, actionCount };
};
